import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface AssetInitialState {
  tree: TreeRoot;
  notifications: NotificationList;
  selectedNode: SelectedNode;
}

export interface NotificationList {
  id: string;
  list: NotificationItem[];
}

export interface IconItem {
  id: string;
  type: string;
  description: string;
}

export interface NotificationItem {
  id: string;
  category: string;
  date: string;
  tag: string;
  content: string;
  iconList: IconItem[];
}

export interface SelectedNode {
  id: string;
  name: string;
  level: number;
  parentId: string;
}

export interface TreeRoot {
  id: string;
  name: string;
  children?: TreeParent[];
}

export interface TreeParent {
  id: string;
  name: string;
  children?: TreeChild[];
}

export interface TreeChild {
  id: string;
  name: string;
  children?: TreeChild1[];
}

export interface TreeChild1 {
  id: string;
  name: string;
  metadata: Record<string, string | number | undefined | null>;
  children?: TreeChild2[];
}

export interface TreeChild2 {
  id: string;
  name: string;
  metadata: Record<string, string | number | undefined | null>;
}

const initialState: AssetInitialState = {
  tree: {
    id: "root",
    name: "",
    children: [
      {
        id: "BO1",
        name: "Borneo",
        children: [
          {
            id: "1BO1",
            name: "Borneo 1",
            children: [
              {
                id: "1PF1",
                name: "Processing Facility 1",
                metadata: { type: "Onshore" },
                children: [],
              },
              {
                id: "1PF2",
                name: "Processing Facility 2",
                metadata: { type: "Onshore" },
                children: [],
              },
              {
                id: "PF3",
                name: "Processing Facility 3",
                metadata: { type: "Onshore" },
                children: [],
              },
              {
                id: "1AL1",
                name: "Alpha",
                metadata: { type: "Offshore" },
                children: [
                  {
                    id: "A1",
                    name: "A1",
                    metadata: { "well-type": "Shut-In", "lift-method": "Gas Lift" },
                  },
                  {
                    id: "A2",
                    name: "A2",
                    metadata: { "well-type": "Water Disposal", "lift-method": "-" },
                  },
                  {
                    id: "A3",
                    name: "A3",
                    metadata: { "well-type": "Oil", "lift-method": "Gas Lift" },
                  },
                  {
                    id: "A4",
                    name: "A4",
                    metadata: { "well-type": "Gas", "lift-method": "Plunger Lift" },
                  },
                  {
                    id: "A5",
                    name: "A5",
                    metadata: { "well-type": "Oil", "lift-method": "Natural Flow" },
                  },
                  {
                    id: "A6",
                    name: "A6",
                    metadata: { "well-type": "Oil", "lift-method": "Electrical Submersible Pump" },
                  },
                  {
                    id: "A7",
                    name: "A7",
                    metadata: { "well-type": "Oil", "lift-method": "Natural Flow" },
                  },
                  {
                    id: "A8",
                    name: "A8",
                    metadata: { "well-type": "Gas", "lift-method": "Natural Flow" },
                  },
                ],
              },
              {
                id: "1BR1",
                name: "Bravo",
                metadata: { type: "Onshore" },
                children: [],
              },
            ],
          },
        ],
      },
      {
        id: "CE1",
        name: "Celebes",
        children: [
          {
            id: "1CE1",
            name: "Celebes 1",
            children: [
              {
                id: "2PF1",
                name: "Processing Facility 1",
                metadata: { type: "Onshore" },
                children: [],
              },
              {
                id: "2PF2",
                name: "Processing Facility 2",
                metadata: { type: "Offshore" },
                children: [],
              },
              {
                id: "2CH1",
                name: "Charlie",
                metadata: { type: "Onshore" },
                children: [
                  {
                    id: "C1",
                    name: "C1",
                    metadata: { "well-type": "Shut-In", "lift-method": "Natural Flow" },
                  },
                  {
                    id: "C2",
                    name: "C2",
                    metadata: { "well-type": "Shut-In", "lift-method": "Natural Flow" },
                  },
                  {
                    id: "C3",
                    name: "C3",
                    metadata: { "well-type": "Gas", "lift-method": "Natural Flow" },
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  notifications: {
    id: "root",
    list: [
      {
        id: "A1",
        category: "Unresolved",
        date: "May 1st, 2023",
        tag: "Supervisor",
        content:
          "A total of 3 welltest(s) data added in April 24th has not been approved",
        iconList: [
          {
            id: "W1",
            type: "Oil",
            description: "A3",
          },
          {
            id: "W2",
            type: "Gas",
            description: "A4",
          },
          {
            id: "W3",
            type: "Oil",
            description: "A5",
          },
        ],
      },
      {
        id: "A2",
        category: "Unresolved",
        date: "May 1st, 2023",
        tag: "All",
        content:
          "This week, a total of 2 well BHP has been declining significantly",
        iconList: [
          {
            id: "W4",
            type: "Gas",
            description: "B3",
          },
          {
            id: "W5",
            type: "Gas",
            description: "A8",
          },
        ],
      },
      {
        id: "A3",
        category: "Resolved",
        date: "April 30th, 2023",
        tag: "Operator",
        content:
          "B4 : The most recent welltest data inputted has several inconsistencies compared to previous data",
        iconList: [
          {
            id: "W6",
            type: "Double Up Arrow",
            description: "WHP",
          },
          {
            id: "W7",
            type: "Down Arrow",
            description: "WHT",
          },
        ],
      },
      {
        id: "A4",
        category: "Unresolved",
        date: "April 29th, 2023",
        tag: "All",
        content:
            "This week, a total of 2 well BHP has been declining significantly",
        iconList: [
          {
            id: "W8",
            type: "Gas",
            description: "B3",
          },
          {
            id: "W9",
            type: "Gas",
            description: "A8",
          },
        ],
      },
    ],
  },
  selectedNode: {
    id: "",
    name: "",
    level: 0,
    parentId: "",
  },
};
export const assetSlice = createSlice({
  name: "asset",
  initialState,
  reducers: {
    addChild: (state, action: PayloadAction<AddAssetProps>) => {
      if (action.payload.level === 1) {
        state.tree.children! = [
          ...state.tree.children!,
          {
            id: action.payload.id,
            name: action.payload.name,
            children: [],
          },
        ];
      } else if (action.payload.level === 2) {
        const realTreeData = state.tree.children!;
        for (let i = 0; i < realTreeData.length; i++) {
          if (realTreeData[i].id === action.payload.parentId) {
            if (state.tree.children![i].hasOwnProperty("children")) {
              state.tree.children![i].children!.push({
                id: action.payload.id,
                name: action.payload.name,
                children: [],
              });
            } else {
              state.tree.children![i].children! = [
                {
                  id: action.payload.id,
                  name: action.payload.name,
                  children: [],
                },
              ];
            }
            break;
          }
        }
      } else if (action.payload.level === 3) {
        let finish = false;
        const realTreeData = state.tree.children!;
        for (let i = 0; i < realTreeData.length; i++) {
          if (finish) {
            break;
          }
          if (realTreeData[i].hasOwnProperty("children")) {
            for (let j = 0; j < realTreeData[i].children!.length; j++) {
              if (finish) {
                break;
              }
              if (realTreeData[i].children![j].id === action.payload.parentId) {
                if (
                  state.tree.children![i].children![j].hasOwnProperty(
                    "children"
                  )
                ) {
                  state.tree.children![i].children![j].children!.push({
                    id: action.payload.id,
                    name: action.payload.name,
                    metadata: action.payload.metadata!,
                  });
                } else {
                  state.tree.children![i].children![j].children! = [
                    {
                      id: action.payload.id,
                      name: action.payload.name,
                      metadata: action.payload.metadata!,
                    },
                  ];
                }
                finish = true;
                break;
              }
            }
          }
        }
      } else if (action.payload.level === 4) {
        let finish = false;
        const realTreeData = state.tree.children!;
        for (let i = 0; i < realTreeData.length; i++) {
          if (finish) {
            break;
          }
          if (realTreeData[i].hasOwnProperty("children")) {
            for (let j = 0; j < realTreeData[i].children!.length; j++) {
              if (finish) {
                break;
              }
              for (let k = 0; k < realTreeData[i].children![j].children!.length; k++) {
                if (finish) {
                  break;
                }
                if (realTreeData[i].children![j].children![k].id === action.payload.parentId) {
                  if (
                    state.tree.children![i].children![j].children![k].hasOwnProperty(
                      "children"
                    )
                  ) {
                    state.tree.children![i].children![j].children![k].children!.push({
                      id: action.payload.id,
                      name: action.payload.name,
                      metadata: action.payload.metadata!,
                    });
                  } else {
                    state.tree.children![i].children![j].children![k].children! = [
                      {
                        id: action.payload.id,
                        name: action.payload.name,
                        metadata: action.payload.metadata!,
                      },
                    ];
                  }
                  finish = true;
                  break;
                }
              }
            }
          }
        }
      }
    },
    changeSelectedNode: (state, action: PayloadAction<SelectedNode>) => {
      state.selectedNode = action.payload;
    },
    editNode: (state, action: PayloadAction<AddAssetProps>) => {
      if (action.payload.level === 1) {
        const realTreeData = state.tree.children!;
        for (let i = 0; i < realTreeData.length; i++) {
          if (realTreeData[i].id === action.payload.id) {
            state.tree.children![i] = {
              id: action.payload.id,
              name: action.payload.name,
              children: state.tree.children![i].children,
            };
            break;
          }
        }
      } else if (action.payload.level === 2) {
        let finish = false;
        const realTreeData = state.tree.children!;
        for (let i = 0; i < realTreeData.length; i++) {
          if (finish) {
            break;
          }
          if (realTreeData[i].id === action.payload.parentId) {
            for (let j = 0; j < realTreeData[i].children!.length; j++) {
              if (realTreeData[i].children![j].id === action.payload.id) {
                state.tree.children![i].children![j] = {
                  id: action.payload.id,
                  name: action.payload.name,
                  children: state.tree.children![i].children![j].children,
                };
                finish = true;
                break;
              }
            }
          }
        }
      } else if (action.payload.level === 3) {
        let finish = false;
        const realTreeData = state.tree.children!;
        for (let i = 0; i < realTreeData.length; i++) {
          if (finish) {
            break;
          }
          if (realTreeData[i].hasOwnProperty("children")) {
            for (let j = 0; j < realTreeData[i].children!.length; j++) {
              if (finish) {
                break;
              }
              if (realTreeData[i].children![j].hasOwnProperty("children")) {
                for (
                  let k = 0;
                  k < realTreeData[i].children![j].children!.length;
                  k++
                ) {
                  if (
                    realTreeData[i].children![j].children![k].id ===
                    action.payload.id
                  ) {
                    state.tree.children![i].children![j].children![k] = {
                      id: action.payload.id,
                      name: action.payload.name,
                      metadata: action.payload.metadata!,
                    };
                    finish = true;
                    break;
                  }
                }
              }
            }
          }
        }
      } else if (action.payload.level === 4) {
        let finish = false;
        const realTreeData = state.tree.children!;
        for (let i = 0; i < realTreeData.length; i++) {
          if (finish) {
            break;
          }
          if (realTreeData[i].hasOwnProperty("children")) {
            for (let j = 0; j < realTreeData[i].children!.length; j++) {
              if (finish) {
                break;
              }
              if (realTreeData[i].children![j].hasOwnProperty("children")) {
                for (let k = 0; k < realTreeData[i].children![j].children!.length; k++) {
                  if (finish) {
                    break;
                  }
                  if (realTreeData[i].children![j].children![k].hasOwnProperty("children")) {
                    for (let l = 0; l < realTreeData[i].children![j].children![k].children!.length; l++) {
                      if (
                        realTreeData[i].children![j].children![k].children![l].id ===
                        action.payload.id
                      ) {
                        state.tree.children![i].children![j].children![k].children![l] = {
                          id: action.payload.id,
                          name: action.payload.name,
                          metadata: action.payload.metadata!,
                        };
                        finish = true;
                        break;
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    },
    deleteSelectedNode: (state, action: PayloadAction<DeleteAssetProps>) => {
      if (action.payload.level === 1) {
        state.tree.children! = state.tree.children!.filter((item) => {
          return item.id !== action.payload.id;
        });
      } else if (action.payload.level === 2) {
        const realTreeData = state.tree.children!;
        for (let i = 0; i < realTreeData.length; i++) {
          if (realTreeData[i].id === action.payload.parentId) {
            state.tree.children![i].children! = state.tree.children![
              i
            ].children!.filter((item) => {
              return item.id !== action.payload.id;
            });
            break;
          }
        }
      } else if (action.payload.level === 3) {
        let finish = false;
        const realTreeData = state.tree.children!;
        for (let i = 0; i < realTreeData.length; i++) {
          if (finish) {
            break;
          }
          if (realTreeData[i].hasOwnProperty("children")) {
            for (let j = 0; j < realTreeData[i].children!.length; j++) {
              if (realTreeData[i].children![j].id === action.payload.parentId) {
                state.tree.children![i].children![j].children! =
                  state.tree.children![i].children![j].children!.filter(
                    (item) => {
                      return item.id !== action.payload.id;
                    }
                  );
                finish = true;
                break;
              }
            }
          }
        }
      } else if (action.payload.level === 4) {
        let finish = false;
        const realTreeData = state.tree.children!;
        for (let i = 0; i < realTreeData.length; i++) {
          if (finish) {
            break;
          }
          if (realTreeData[i].hasOwnProperty("children")) {
            for (let j = 0; j < realTreeData[i].children!.length; j++) {
              if (finish) {
                break;
              }
              if (realTreeData[i].children![j].hasOwnProperty("children")) {
                for (let k = 0; k < realTreeData[i].children![j].children!.length; k++) {
                  if (realTreeData[i].children![j].children![k].id === action.payload.parentId) {
                    state.tree.children![i].children![j].children![k].children! =
                      state.tree.children![i].children![j].children![k].children!.filter(
                        (item) => {
                          return item.id !== action.payload.id;
                        }
                      );
                    finish = true;
                    break;
                  }
                }
              }
            }
          }
        }
      }
    },
    deleteAllNode: (state) => {
      state.tree.children = [];
    },
  },
});

export const {
  addChild,
  changeSelectedNode,
  editNode,
  deleteSelectedNode,
  deleteAllNode,
} = assetSlice.actions;
export default assetSlice.reducer;
